import type { MenuNode } from "./site-menu";
import type { SiteTarget } from "./site-target";

const PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);
const MAX_ADDRESS_LENGTH = 500;

/** Checks an address the owner typed for an outside link: http, https, mailto or tel only (never javascript: or data:), no login inside, not too long. */
export function verifyExternalAddress(value: string): { ok: true } | { ok: false; code: "site-address-invalid" } {
  const bad = { ok: false, code: "site-address-invalid" } as const;
  if (value.length === 0 || value.length > MAX_ADDRESS_LENGTH || !URL.canParse(value)) return bad;
  const url = new URL(value);
  if (!PROTOCOLS.has(url.protocol) || url.username !== "" || url.password !== "") return bad;
  return { ok: true };
}

const MAX_ROUTE_LENGTH = 200;

/** Checks the address of a page of this app: it starts with one "/", has no second "/" or "\" right after it (that would leave the site), no protocol, no space. The language is added when the link is drawn. */
export function verifyRoutePath(value: string): { ok: true } | { ok: false; code: "site-route-invalid" } {
  const bad = { ok: false, code: "site-route-invalid" } as const;
  if (value.length > MAX_ROUTE_LENGTH || !/^\/(?![/\\])[^\s:\\]*$/.test(value.split(/[?#]/)[0] ?? "")) return bad;
  return /\s/.test(value) ? bad : { ok: true };
}

/** Checks one target the way the server will, so the editor can say what is wrong before saving. */
export function verifySiteTarget(target: SiteTarget): { ok: true } | { ok: false; code: "site-address-invalid" | "site-route-invalid" | "site-page-missing" } {
  if (target.type === "external") return verifyExternalAddress(target.url);
  if (target.type === "route") return verifyRoutePath(target.path);
  if (target.type === "page" && !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(target.slug)) return { ok: false, code: "site-page-missing" };
  return { ok: true };
}

const MAX_DEPTH = 4;
const MAX_NODES = 500;

type Result = { ok: true } | { ok: false; code: "site-menu-too-deep" | "site-menu-duplicate-id" | "site-menu-too-big" };

function walk(nodes: readonly MenuNode[], depth: number, ids: string[]): boolean {
  ids.push(...nodes.map((n) => n.id));
  return depth <= MAX_DEPTH && nodes.every((n) => !n.children || walk(n.children, depth + 1, ids));
}

/** Checks the shape of a list of menu entries: groups nested at most three levels (group, submenu, link), every id used once, at most 500 entries in all. */
export function verifyMenuShape(nodes: readonly MenuNode[]): Result {
  const ids: string[] = [];
  if (!walk(nodes, 1, ids)) return { ok: false, code: "site-menu-too-deep" };
  if (ids.length > MAX_NODES) return { ok: false, code: "site-menu-too-big" };
  return new Set(ids).size === ids.length ? { ok: true } : { ok: false, code: "site-menu-duplicate-id" };
}
